/* Shared "remove connection" UI — a hover-revealed kebab menu + a hardened danger confirm dialog,
   used by BOTH delete surfaces: the auth.js connection picker (its own pre-app overlay) and the
   tenants.js hosted connection dropdown.

   WHY A STANDALONE MODULE. auth.js is deliberately one-directional — it never imports api.js (api.js
   imports auth.js). Putting the shared confirm in dialogs.js (which imports api.js) would force
   auth.js to import it and create an auth.js → dialogs.js → api.js → auth.js cycle. This module
   imports only esc (state.js), so both surfaces can share it without breaking that boundary. It does
   the UI only (menu + confirm); each caller performs the actual DELETE + routing itself.

   The confirm is a native <dialog> shown via showModal(), so it renders in the top layer — above the
   app AND above the picker overlay's z-index. It mirrors the app's hardened-dialog conventions:
   danger-styled action, Cancel default-focused, Esc/Cancel close it — a native <dialog> ignores
   backdrop clicks (intentional for a destructive action, so there is no backdrop-cancel handler);
   claim-and-null resolver with a stale-close guard — and, for a prod connection (env_role === "prod"),
   a typed-confirm gate that holds the danger button disabled until the connection label is typed
   (mirrors rewind-dialog.js). */

import { esc } from './state.js';

// ── one-time self-contained styling (menu + dialog identity/prod-gate bits) ───────────────────
function _injectStyleOnce() {
  if (document.getElementById("cdelStyle")) return;
  const s = document.createElement("style");
  s.id = "cdelStyle";
  s.textContent = `
    .cdel-menu{position:fixed;z-index:100000;min-width:184px;padding:6px;
      background:var(--surface,#fff);color:var(--ink,#1f1f1f);
      border:1px solid var(--line,#e4e7ec);border-radius:10px;
      box-shadow:0 10px 30px rgba(0,0,0,.18),0 2px 8px rgba(0,0,0,.08);}
    .cdel-mi{display:flex;align-items:center;gap:10px;width:100%;padding:8px 10px;border:0;
      background:transparent;border-radius:7px;font:inherit;font-size:13px;text-align:left;
      cursor:pointer;color:var(--error,#D93025);}
    .cdel-mi:hover{background:var(--error-bg,#FCECEA);}
    .cdel-mi .ic{color:var(--error,#D93025);flex:none;}
    .cdel-dialog .confirm-body{max-width:440px;}
    .cdel-identity{display:flex;align-items:center;flex-wrap:wrap;gap:8px;margin:2px 0 10px;}
    .cdel-label{font-weight:600;font-size:14px;color:var(--ink,#1f1f1f);}
    .cdel-host{font-size:12px;color:var(--muted,#5F6368);}
    .cdel-env{font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;
      padding:2px 6px;border-radius:999px;background:var(--hover-bg,#EEF1FF);color:var(--muted,#5F6368);}
    .cdel-env.prod{background:#FCECEA;color:#D93025;}
    .cdel-prod-gate{margin:10px 0 2px;}
    .cdel-prod-warn{margin:0 0 6px;font-size:12px;color:var(--error,#D93025);display:flex;align-items:center;gap:6px;}
    .cdel-typed{width:100%;box-sizing:border-box;padding:8px 10px;font:inherit;font-size:13px;
      border:1px solid var(--line,#e4e7ec);border-radius:8px;outline:none;background:var(--surface,#fff);color:var(--ink,#1f1f1f);}
    .cdel-typed:focus{border-color:var(--sap-blue,#1A73E8);}
  `;
  document.head.appendChild(s);
}

// ── kebab popover menu (a single "Remove connection" danger item) ─────────────────────────────
let _menu = null;

/** Close any open remove-menu. Safe to call when none is open. */
export function closeRemoveMenu() {
  if (_menu) { _menu.remove(); _menu = null; }
}

/**
 * Open the remove-menu anchored to a kebab button. The CALLER's kebab click handler MUST
 * stopPropagation() so the opening click doesn't immediately hit the document close-listener below.
 * @param {HTMLElement} anchorEl the kebab button clicked
 * @param {() => void} onRemove invoked when "Remove connection" is chosen
 */
export function openRemoveMenu(anchorEl, onRemove) {
  _injectStyleOnce();
  closeRemoveMenu();
  const m = document.createElement("div");
  m.className = "cdel-menu";
  m.setAttribute("role", "menu");
  m.innerHTML = `<button type="button" class="cdel-mi" role="menuitem">`
    + `<i class="ic ic-sm ic-trash-2" aria-hidden="true"></i><span>Remove connection</span></button>`;
  document.body.appendChild(m);
  // Fixed placement computed from the button's on-screen rect — escapes any clipping ancestor
  // (the dropdown / picker), right-aligned, flipped above when there isn't room below.
  const r = anchorEl.getBoundingClientRect();
  const mh = m.offsetHeight, mw = m.offsetWidth, gap = 4;
  const top = (r.bottom + gap + mh <= window.innerHeight) ? r.bottom + gap : Math.max(4, r.top - gap - mh);
  m.style.top = `${top}px`;
  m.style.left = `${Math.max(4, r.right - mw)}px`;
  m.querySelector(".cdel-mi").addEventListener("click", (e) => {
    e.stopPropagation();
    closeRemoveMenu();
    onRemove();
  });
  _menu = m;
}

// Registered once (ES module top-level runs a single time): any outside click / Esc closes the
// menu. Kebab openers stopPropagation() so their opening click never reaches this listener.
// Guarded so plain-node unit tests can import the render chain (agent-parts.test.mjs et al.)
// without a DOM — the listeners are browser-only wiring.
if (typeof document !== "undefined") {
  document.addEventListener("click", () => closeRemoveMenu());
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeRemoveMenu(); });
}

// ── hardened danger confirm dialog ────────────────────────────────────────────────────────────
let _dlg = null;

function _ensureDialog() {
  if (_dlg) return _dlg;
  _injectStyleOnce();
  const d = document.createElement("dialog");
  d.className = "confirm-dialog cdel-dialog";
  d.innerHTML = `
    <div class="confirm-body">
      <h3>Remove connection?</h3>
      <div class="cdel-identity">
        <span class="cdel-label"></span>
        <span class="cdel-env" hidden></span>
        <span class="cdel-host"></span>
      </div>
      <p class="cdel-consequence"></p>
      <div class="cdel-prod-gate" hidden>
        <p class="cdel-prod-warn"><i class="ic ic-sm ic-alert-triangle" aria-hidden="true"></i><span class="cdel-prod-text"></span></p>
        <input class="cdel-typed" type="text" autocomplete="off" spellcheck="false" />
      </div>
      <div class="confirm-btns">
        <button type="button" class="btn cdel-cancel">Cancel</button>
        <button type="button" class="btn btn-danger cdel-go">Remove connection</button>
      </div>
    </div>`;

  const cancelBtn = d.querySelector(".cdel-cancel");
  const goBtn = d.querySelector(".cdel-go");
  const claim = () => { const r = d._currentResolve; d._currentResolve = null; return r; };

  cancelBtn.addEventListener("click", () => { const r = claim(); d.close(); r?.(false); });
  goBtn.addEventListener("click", () => {
    if (goBtn.disabled) return;
    // For a prod connection the typed-confirm gate is visible; capture what was typed so the caller
    // can forward it to the server as `typed_confirm` (the server re-checks it — the UI gate is only
    // fast feedback). null when there is no prod gate (non-prod).
    const gate = d.querySelector(".cdel-prod-gate");
    const typedConfirm = (gate && !gate.hidden) ? (d.querySelector(".cdel-typed").value.trim() || null) : null;
    const r = claim(); d.close(); r?.({ typedConfirm });
  });
  d.addEventListener("close", () => {
    if (d.open) return;      // stale close from a previous dialog after a reopen
    claim()?.(false);        // Esc / Cancel → cancel (native <dialog> ignores backdrop clicks)
  });
  // No Enter-to-confirm binding: a destructive action should never fire on a stray Return.

  document.body.appendChild(d);
  _dlg = d;
  return d;
}

/**
 * Show the "Remove connection?" confirm. Resolves `false` to cancel; on confirm resolves an object
 * `{ typedConfirm }` — the trimmed prod typed-confirm string for a prod connection (to forward to the
 * server as `typed_confirm`), else `null` for a non-prod connection. Truthy-on-confirm either way, so
 * callers gate with `if (!res) return;` and then read `res.typedConfirm`.
 * @param {{label?:string, host?:string, envRole?:string, isActive?:boolean}} meta
 */
export function confirmRemoveConnection(meta = {}) {
  const d = _ensureDialog();
  const label = meta.label || meta.host || "this connection";
  const envRole = (meta.envRole || "").toLowerCase();
  const isProd = envRole === "prod";

  const q = (sel) => d.querySelector(sel);
  q(".cdel-label").textContent = label;
  const envEl = q(".cdel-env");
  envEl.textContent = envRole;
  envEl.hidden = !envRole;
  envEl.classList.toggle("prod", isProd);
  q(".cdel-host").textContent = meta.host || "";

  // Honest consequences — every clause via textContent (label is user data).
  const parts = ["Permanently removes this connection and its saved SAP credentials."];
  if (meta.isActive) parts.push("You'll be switched out of this connection.");
  parts.push("Its saved conversations will also be deleted.");
  parts.push("This can't be undone.");
  q(".cdel-consequence").textContent = parts.join(" ");

  const gate = q(".cdel-prod-gate");
  const typed = q(".cdel-typed");
  const goBtn = q(".cdel-go");
  if (isProd) {
    gate.hidden = false;
    q(".cdel-prod-text").textContent = `Production environment — type "${label}" to confirm.`;
    typed.value = "";
    typed.placeholder = label;
    goBtn.disabled = true;
    const check = () => { goBtn.disabled = typed.value.trim() !== label.trim(); };
    typed.addEventListener("input", check);
    d.addEventListener("close", () => typed.removeEventListener("input", check), { once: true });
  } else {
    gate.hidden = true;
    typed.value = "";
    goBtn.disabled = false;
  }

  return new Promise((resolve) => {
    d._currentResolve = resolve;
    d.showModal();
    // Default focus on Cancel (not the danger button) — the safe default for a destructive dialog.
    (isProd ? typed : d.querySelector(".cdel-cancel"))?.focus();
  });
}
